import { getDateTimeUtcString } from '@planning/utils'
import dayjs, { Dayjs } from 'dayjs'

type TimingsDatetimeType = {
  etaTime?: Dayjs | null
  etbTime?: Dayjs | null
  etdTime?: Dayjs | null
  etaDate?: Dayjs | null
  etbDate?: Dayjs | null
  etdDate?: Dayjs | null
}

type TimingsDateType = Pick<TimingsDatetimeType, 'etaDate' | 'etbDate' | 'etdDate'>

const getTimingDatetimes = (props: TimingsDatetimeType) => {
  const { etaTime, etbTime, etdTime, etaDate, etbDate, etdDate } = props

  const etaUtc = getDateTimeUtcString(etaDate, etaTime)
  const etbUtc = !!etbDate || !!etbTime ? getDateTimeUtcString(etbDate, etbTime) : null
  const etdUtc = getDateTimeUtcString(etdDate, etdTime)
  const dayjsEta = dayjs(etaUtc)
  const dayjsEtb = dayjs(etbUtc)
  const dayjsEtd = dayjs(etdUtc)

  return {
    etaDate: dayjsEta,
    etbDate: dayjsEtb,
    etdDate: dayjsEtd,
  }
}

// Reject dates more than `yearsBack` years before today or `yearsForward` years after.
// Mirrors the backend `WithinReasonableRange` validator. Null/undefined passes.
export const validateReasonableDate = (
  date: Dayjs | null | undefined,
  yearsBack = 5,
  yearsForward = 5,
) => {
  if (!date) return
  const now = dayjs()
  if (date.isBefore(now.subtract(yearsBack, 'year'))) return 'dateOutOfReasonableRange'
  if (date.isAfter(now.add(yearsForward, 'year'))) return 'dateOutOfReasonableRange'
}

// Estimated time of arrival (ETA)
export const validateEtaDate = (props: TimingsDateType) => {
  const { etaDate, etbDate, etdDate } = props

  if (!!etbDate && etaDate?.isAfter(etbDate, 'day')) return 'etaAfterEtb'
  if (etaDate?.isAfter(etdDate, 'day')) return 'etaAfterEtd'
}

export const validateEtaDatetime = (props: TimingsDatetimeType) => {
  const timings = getTimingDatetimes(props)

  if (!!timings.etbDate && timings.etaDate?.isAfter(timings.etbDate)) return 'etaAfterEtb'
  if (timings.etaDate?.isAfter(timings.etdDate)) return 'etaAfterEtd'
}

// Estimated time of berthing (ETB)
export const validateEtbDate = (props: TimingsDateType) => {
  const { etaDate, etbDate, etdDate } = props

  if (etbDate?.isBefore(etaDate, 'day')) return 'etbBeforeEta'
  if (etbDate?.isAfter(etdDate, 'day')) return 'etbAfterEtd'
}

export const validateEtbDatetime = (props: TimingsDatetimeType) => {
  const timings = getTimingDatetimes(props)

  if (!!timings.etbDate && timings.etbDate?.isBefore(timings.etaDate)) return 'etbBeforeEta'
  if (timings.etbDate?.isAfter(timings.etdDate)) return 'etbAfterEtd'
}

// Estimated time of departure (ETD)
export const validateEtdDate = (props: TimingsDateType) => {
  const { etaDate, etbDate, etdDate } = props

  if (etdDate?.isBefore(etaDate, 'day')) return 'etdBeforeEta'
  if (!!etbDate && etdDate?.isBefore(etbDate, 'day')) return 'etdBeforeEtb'
}

export const validateEtdDatetime = (props: TimingsDatetimeType) => {
  const timings = getTimingDatetimes(props)

  if (timings.etdDate?.isBefore(timings.etaDate)) return 'etdBeforeEta'
  if (!!timings.etbDate && timings.etdDate?.isBefore(timings.etbDate)) return 'etdBeforeEtb'
}
